// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selection Card Component

import { useState } from 'react';
import { EnhancedSelection } from '@/types/selection';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { SelectionBadge } from './SelectionBadge';
import { SelectionTagList } from './SelectionTag';
import { TraitRadarChart } from './TraitRadarChart';
import { PercentileGrid } from './PercentileIndicator';
import { 
  MoreHorizontal, 
  ChevronDown, 
  ChevronUp, 
  Image, 
  Edit, 
  Trash2, 
  Maximize, 
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface SelectionCardProps {
  selection: EnhancedSelection;
  compareSelections?: EnhancedSelection[];
  percentileData?: Record<string, number[]>;
  className?: string;
  onEdit?: (selection: EnhancedSelection) => void;
  onDelete?: (selection: EnhancedSelection) => void;
  onViewDetails?: (selection: EnhancedSelection) => void;
  onCompare?: (selection: EnhancedSelection) => void;
}

export function SelectionCard({
  selection,
  compareSelections = [],
  percentileData = {},
  className = '',
  onEdit,
  onDelete,
  onViewDetails,
  onCompare
}: SelectionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [comparisonSelection, setComparisonSelection] = useState<EnhancedSelection | undefined>();
  
  // Calculate growth metrics with percentiles
  const growthMetrics = [
    {
      label: 'Structure',
      value: selection.growthMetrics.structure,
      percentile: calculatePercentile(selection.growthMetrics.structure, percentileData.structure || [])
    },
    {
      label: 'Vigor',
      value: selection.growthMetrics.vigor,
      percentile: calculatePercentile(selection.growthMetrics.vigor, percentileData.vigor || [])
    },
    {
      label: 'Yield',
      value: selection.growthMetrics.yield,
      percentile: calculatePercentile(selection.growthMetrics.yield, percentileData.yield || [])
    },
    {
      label: 'Frost',
      value: selection.growthMetrics.frost,
      percentile: calculatePercentile(selection.growthMetrics.frost, percentileData.frost || [])
    },
    {
      label: 'Overall',
      value: selection.growthMetrics.overall,
      percentile: calculatePercentile(selection.growthMetrics.overall, percentileData.overall || [])
    }
  ];
  
  // Calculate sensory metrics with percentiles if available
  const sensoryMetrics = selection.sensoryData ? [
    {
      label: 'Aroma Intensity',
      value: selection.sensoryData.aroma_intensity || 0,
      percentile: calculatePercentile(
        selection.sensoryData.aroma_intensity || 0, 
        percentileData.aroma_intensity || []
      )
    },
    {
      label: 'Aroma Quality',
      value: selection.sensoryData.aroma_quality || 0,
      percentile: calculatePercentile(
        selection.sensoryData.aroma_quality || 0, 
        percentileData.aroma_quality || []
      )
    },
    {
      label: 'Flavor Intensity',
      value: selection.sensoryData.flavor_intensity || 0,
      percentile: calculatePercentile(
        selection.sensoryData.flavor_intensity || 0, 
        percentileData.flavor_intensity || []
      )
    },
    {
      label: 'Flavor Quality',
      value: selection.sensoryData.flavor_quality || 0,
      percentile: calculatePercentile(
        selection.sensoryData.flavor_quality || 0, 
        percentileData.flavor_quality || []
      )
    },
    {
      label: 'Smoke Quality',
      value: selection.sensoryData.smoke_harshness || 0,
      percentile: calculatePercentile(
        selection.sensoryData.smoke_harshness || 0, 
        percentileData.smoke_harshness || []
      )
    }
  ].filter(metric => metric.value > 0) : [];
  
  // Toggle expanded state
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  // Get top badges
  const topBadges = selection.badges.slice(0, 3);
  
  return (
    <Card className={cn('w-full overflow-hidden', className)}>
      <CardHeader className="p-4 pb-2">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">
                {selection.plantId}
              </CardTitle>
              <Badge variant="outline" className="bg-primary/10 text-primary text-xs">
                {selection.generation}
              </Badge>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Selected on {new Date(selection.selectionDate).toLocaleDateString()}
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onViewDetails && (
                <DropdownMenuItem onClick={() => onViewDetails(selection)}>
                  <ExternalLink className="mr-2 h-4 w-4" /> View Details
                </DropdownMenuItem>
              )}
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(selection)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit Selection
                </DropdownMenuItem>
              )}
              {onCompare && (
                <DropdownMenuItem onClick={() => onCompare(selection)}>
                  <Maximize className="mr-2 h-4 w-4" /> Compare
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(selection)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          {topBadges.map(badge => (
            <SelectionBadge 
              key={badge.id} 
              badge={badge} 
              size="sm" 
            />
          ))}
          
          {selection.badges.length > 3 && (
            <Badge variant="outline" className="text-xs bg-gray-100">
              +{selection.badges.length - 3} more
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-4 pt-0">
        <div className="flex flex-row gap-6">
          {/* Left column: Growth metrics */}
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Growth Metrics</h3>
            <PercentileGrid 
              metrics={growthMetrics}
              colorScale="green"
            />
          </div>
          
          {/* Right column: Image or chart */}
          <div className="w-32 h-32 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center border">
            {selection.images && selection.images.length > 0 ? (
              <img 
                src={selection.images[0]} 
                alt={selection.plantId} 
                className="w-full h-full object-cover"
              />
            ) : (
              <TraitRadarChart 
                selection={selection}
                width={128}
                height={128}
                showLabels={false}
              />
            )}
          </div>
        </div>
        
        {/* Selection highlights */}
        {selection.selectionHighlights && selection.selectionHighlights.length > 0 && (
          <div className="mt-3">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Highlights</h3>
            <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
              {selection.selectionHighlights.slice(0, expanded ? undefined : 3).map((highlight, index) => (
                <li key={index}>{highlight}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Selection rationale */}
        {selection.selectionRationale && (
          <div className="mt-3">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Selection Rationale</h3>
            <p className="text-sm text-gray-600">
              {expanded 
                ? selection.selectionRationale 
                : `${selection.selectionRationale.substring(0, 150)}${selection.selectionRationale.length > 150 ? '...' : ''}`
              }
            </p>
          </div>
        )}
        
        {/* Tags */}
        <div className="mt-3">
          <h3 className="text-sm font-medium text-gray-700 mb-1">Tags</h3>
          <SelectionTagList 
            tags={selection.tags} 
            size="sm" 
            maxDisplay={expanded ? 0 : 8}
          />
        </div>
        
        {/* Expanded content */}
        {expanded && (
          <div className="mt-4">
            <Separator className="mb-4" />
            
            {/* Sensory data if available */}
            {selection.sensoryData && sensoryMetrics.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Sensory Profile</h3>
                <PercentileGrid 
                  metrics={sensoryMetrics}
                  colorScale="purple"
                />
              </div>
            )}
            
            {/* Trait visualization */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium text-gray-700">Trait Visualization</h3>
                
                {/* Comparison selector */}
                {compareSelections.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Compare with:</span>
                    <select 
                      className="text-xs border rounded px-2 py-1"
                      onChange={(e) => {
                        const selected = compareSelections.find(s => s.id === e.target.value);
                        setComparisonSelection(selected);
                      }}
                      value={comparisonSelection?.id || ''}
                    >
                      <option value="">None</option>
                      {compareSelections.map(s => (
                        <option key={s.id} value={s.id}>{s.plantId}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="flex justify-center">
                <TraitRadarChart 
                  selection={selection}
                  compareTo={comparisonSelection}
                  width={320}
                  height={280}
                  showLabels={true}
                  showLegend={!!comparisonSelection}
                />
              </div>
            </div>
            
            {/* Notes */}
            {selection.selectionNotes && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-1">Notes</h3>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                  {selection.selectionNotes}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="p-2 flex justify-center border-t">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleExpanded}
          className="text-xs text-gray-500 hover:text-gray-900 w-full"
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4 mr-1" />
          ) : (
            <ChevronDown className="h-4 w-4 mr-1" />
          )}
          {expanded ? 'Show Less' : 'Show More'}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Helper function to calculate percentile
function calculatePercentile(value: number, dataset: number[]): number {
  if (!dataset.length) return 50; // Default to 50th percentile if no data
  
  const filteredData = dataset.filter(v => v !== null && v !== undefined);
  if (filteredData.length === 0) return 50;
  
  const sortedData = [...filteredData].sort((a, b) => a - b);
  const position = sortedData.findIndex(item => item >= value);
  
  if (position === -1) return 100; // Value is higher than all in dataset
  
  return Math.round((position / sortedData.length) * 100);
} 